/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";

const ConfirmDialog = ({
  open,
  title = "Vahvista",
  description,
  confirmText = "OK",
  cancelText = "Peru",
  onConfirm,
  onClose,
  busy = false,
  busyText = "Suoritetaan…",
}) => {
  const containerRef = useRef(null);
  const confirmRef = useRef(null);
  const cancelRef = useRef(null);

  // uniikit id:t ariaa varten
  const ids = useMemo(() => {
    const base = Math.random().toString(36).slice(2);
    return {
      title: `cdlg_title_${base}`,
      desc: `cdlg_desc_${base}`,
    };
  }, []);

  // Estä sivun scroll, kun modal auki
  useEffect(() => {
    if (!open) return;
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = overflow; };
  }, [open]);

  // Fokusta modaliin, ensimmäinen painike → Confirm
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      (confirmRef.current || containerRef.current)?.focus?.();
    }, 0);
    return () => clearTimeout(t);
  }, [open, busy]);

  // Trapita Tab modaliin
  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape") {
      if (!busy) onClose?.();
      return;
    }
    if (e.key === "Enter") {
      // Salli Enter = vahvista
      if (!busy) onConfirm?.();
      e.preventDefault();
      return;
    }
    if (e.key === "Tab") {
      const root = containerRef.current;
      if (!root) return;
      const focusables = root.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      const items = Array.from(focusables).filter(el => !el.hasAttribute("disabled"));
      if (items.length === 0) return;

      const current = document.activeElement;
      const i = items.indexOf(current);
      let nextIndex = i;

      if (e.shiftKey) {
        nextIndex = i <= 0 ? items.length - 1 : i - 1;
      } else {
        nextIndex = i === items.length - 1 ? 0 : i + 1;
      }
      items[nextIndex]?.focus();
      e.preventDefault();
    }
  }, [busy, onConfirm, onClose]);

  if (!open) return null;

  const ui = (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={ids.title}
      aria-describedby={description ? ids.desc : undefined}
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      onKeyDown={onKeyDown}
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => { if (!busy) onClose?.(); }}
      />
      <div
        ref={containerRef}
        className="relative w-[min(92vw,480px)] rounded-2xl bg-white dark:bg-zinc-900 shadow-2xl ring-1 ring-black/10 dark:ring-white/10 p-5 outline-none"
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
      >
        <h3 id={ids.title} className="text-lg font-semibold text-black/90 dark:text-white/90">
          {title}
        </h3>
        {description && (
          <p id={ids.desc} className="mt-2 text-sm text-black/70 dark:text-white/70">
            {description}
          </p>
        )}

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            ref={cancelRef}
            type="button"
            className="h-10 px-4 rounded-lg border border-black/10 dark:border-white/20 bg-white/70 dark:bg-transparent text-black/80 dark:text-white/80 hover:bg-black/5 disabled:opacity-60"
            onClick={onClose}
            disabled={busy}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className="h-10 px-4 rounded-lg text-white bg-rose-600 hover:bg-rose-500 active:bg-rose-700 disabled:opacity-60"
            onClick={onConfirm}
            disabled={busy}
          >
            {busy ? busyText : confirmText}   {/* <-- UUSI */}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(ui, document.body);
};

export default ConfirmDialog;
